import axios, { AxiosResponse } from 'axios';
import { ENVIRONMENTS, getEnvironment } from '@hotjar/common';
import { captureException } from '../../error-tracking';

import type { UserMessages } from './model';

type UserIdArg = string;

interface PostRequestArgs {
  userId: UserIdArg;
  messageId: string;
}

type ResponseCodeStatus =
  | 'Success'
  | 'BadApiKey'
  | 'BadParams'
  | 'BadJsonBody'
  | 'QueueEmailError'
  | 'GenericError'
  | 'InvalidEmailAddressError'
  | 'DatabaseError'
  | 'EmailAlreadyExists'
  | 'Forbidden';

export interface PostRequestResponse {
  msg: string;
  code: ResponseCodeStatus;
  params?: unknown;
}

interface GetCurrentUserMessagesArgs {
  userId: UserIdArg;
  count?: number;
}

type TrackMessageCloseArgs = PostRequestArgs;
type TrackMessageCloseResponse = PostRequestResponse;

interface TrackMessageClickArgs extends PostRequestArgs {
  clickedUrl: string;
}
type TrackMessageClickResponse = PostRequestResponse;

const API_ROOT = `https://api.iterable.com/api/`;

export const IterableAPI = axios.create({
  baseURL: API_ROOT,
});

IterableAPI.defaults.headers.common['Api-Key'] = process.env.ITERABLE_API_KEY;

IterableAPI.interceptors.response.use(
  (response) => {
    if (response.config.method === 'post') {
      const responseData = response.data as PostRequestResponse;
      if (responseData.code !== 'Success') {
        const responseError = new Error(responseData.msg || responseData.code);
        captureException(responseError, response.data);
        throw responseError;
      }
    }
    return response;
  },
  (error) => {
    captureException(error);

    return Promise.reject(error);
  },
);

/**
 * Retrieves Iterable messages for current logged in user.
 *
 * To avoid displaying Iterable messages in undesired platforms (local env, review env),
 * this method will only perform an API request if some of these conditions are met:
 * - Code is running in live platform: we always want to do a real API request on insights.hotjar.com
 * - `FORCE_REQUEST_ITERABLE_API` env var is set: in case it's needed to force an API request by any reason
 * (e.g. developing Iterable widget locally). Just set it with some value within `.env` file
 * - "FORCE_REQUEST_ITERABLE_API" cookie is set to 1: similar to env var idea, but for Cypress tests
 * (since we can't set env vars per tests, we need to do it over cookies).
 */
export const getCurrentUserMessages = async ({
  userId,
  count = 100,
}: GetCurrentUserMessagesArgs): Promise<AxiosResponse<UserMessages>> => {
  const shouldReachRealAPI = [
    getEnvironment('INSIGHTS') === ENVIRONMENTS.LIVE,
    process.env.FORCE_REQUEST_ITERABLE_API,
    document.cookie.indexOf('FORCE_REQUEST_ITERABLE_API=1') > -1,
  ].some(Boolean);

  if (shouldReachRealAPI) {
    const response = await IterableAPI.get<UserMessages>(`/inApp/getMessages`, {
      params: {
        userId,
        count,
      },
    });
    return response;
  }

  // Mimic axios response with empty Iterable messages if conditions for connecting to real API are not met.
  return Promise.resolve({
    data: { inAppMessages: [] },
    status: 200,
    statusText: '',
    headers: [],
    config: {},
  });
};

export const consumeMessage = async (args: PostRequestArgs): Promise<void> => {
  await IterableAPI.post<PostRequestResponse>(`/events/inAppConsume`, args);
};

/**
 * Creates an inAppDelivery event for a specific user and in-app message.
 *
 * https://api.iterable.com/api/docs#events_trackInAppDelivery
 */
export async function trackMessageDelivery(args: PostRequestArgs): Promise<void> {
  await IterableAPI.post<PostRequestResponse>('/events/trackInAppDelivery', args);
}

/**
 * Creates an inAppDelivery event for a specific user and in-app message.
 *
 * https://api.iterable.com/api/docs#events_trackInAppOpen
 */
export async function trackMessageOpen(args: PostRequestArgs): Promise<void> {
  await IterableAPI.post<PostRequestResponse>('/events/trackInAppOpen', args);
}

/**
 * Creates an inAppClose event for a specific user and in-app message.
 *
 * https://api.iterable.com/api/docs#events_trackInAppClose
 */
export async function trackMessageClose(args: TrackMessageCloseArgs): Promise<void> {
  await IterableAPI.post<TrackMessageCloseResponse>('/events/trackInAppClose', args);
}

/**
 * Creates an inAppClick event for a specific user, in-app message and url.
 *
 * https://api.iterable.com/api/docs#events_trackInAppClick
 */
export async function trackMessageClick(args: TrackMessageClickArgs): Promise<void> {
  await IterableAPI.post<TrackMessageClickResponse>('/events/trackInAppClick', args);
}
